import { useState } from 'react'
import { Dialog } from 'radix-ui'
import { Check, FileText, MessageSquare, X } from 'lucide-react'
import { VERIFICATION_MODE_LABELS, type VerificationMode } from '@/features/contract/types'
import type { SubjectType } from '../types'
import { useFieldVerificationDetail } from '../hooks'
import { RevokeVerificationDialog } from './RevokeVerificationDialog'

export function VerificationDetailsModal({
  verificationId,
  subjectType,
  subjectId,
  fieldLabel,
  canRevoke,
  onClose,
}: {
  verificationId: number
  subjectType: SubjectType
  subjectId: number
  fieldLabel: string
  canRevoke: boolean
  onClose: () => void
}) {
  const detail = useFieldVerificationDetail(verificationId)
  const [showRevoke, setShowRevoke] = useState(false)

  return (
    <>
      <Dialog.Root open onOpenChange={o => { if (!o) onClose() }}>
        <Dialog.Portal>
          <Dialog.Overlay className="fixed inset-0 z-40 bg-slate-950/50 backdrop-blur-[2px]" />
          <Dialog.Content className="fixed left-1/2 top-1/2 z-50 w-full max-w-lg -translate-x-1/2 -translate-y-1/2 rounded-xl bg-white shadow-2xl flex flex-col max-h-[85vh]">
            <header className="px-5 py-3 border-b border-slate-200 flex items-start justify-between gap-4">
              <div className="min-w-0">
                <Dialog.Title className="text-base font-semibold text-slate-900 truncate">
                  Weryfikacja pola
                </Dialog.Title>
                <Dialog.Description className="text-xs text-slate-500 mt-0.5 truncate">
                  „{fieldLabel}”
                </Dialog.Description>
              </div>
              <button
                type="button"
                onClick={onClose}
                className="size-8 rounded-full text-slate-400 hover:bg-slate-100 hover:text-slate-700 flex items-center justify-center shrink-0"
                aria-label="Zamknij"
              >
                <X size={16} />
              </button>
            </header>

            <div className="flex-1 overflow-auto px-5 py-4">
              {detail.isLoading && (
                <p className="text-sm text-slate-500">Wczytywanie…</p>
              )}
              {detail.isError && (
                <p className="text-sm text-red-600">Nie udało się wczytać szczegółów.</p>
              )}
              {detail.data && (
                <div className="space-y-4">
                  <Row k="Zatwierdził">
                    <span className="font-medium text-slate-900">
                      {detail.data.verifiedByName ?? `użytkownik #${detail.data.verifiedByUserId}`}
                    </span>
                    <span className="ml-2 text-xs text-slate-500">
                      {new Date(detail.data.verifiedAt).toLocaleString('pl-PL')}
                    </span>
                  </Row>

                  <Row k="Tryby weryfikacji">
                    <div className="flex flex-wrap gap-1.5">
                      {detail.data.usedModes.map(m => <ModeBadge key={m} mode={m} />)}
                    </div>
                  </Row>

                  {detail.data.verifiedValueSnapshot && (
                    <Row k="Wartość zatwierdzona">
                      <code className="rounded bg-slate-100 px-2 py-0.5 text-[13px] font-mono text-slate-800">
                        {detail.data.verifiedValueSnapshot}
                      </code>
                    </Row>
                  )}

                  {detail.data.evidenceFileIds.length > 0 && (
                    <Row k="Pliki dowodowe">
                      <span className="text-slate-700">
                        {detail.data.evidenceFileIds.length} {pluralPliki(detail.data.evidenceFileIds.length)}
                        <span className="ml-2 text-xs text-slate-400 font-mono">
                          (ID: {detail.data.evidenceFileIds.join(', ')})
                        </span>
                      </span>
                    </Row>
                  )}

                  {detail.data.evidenceComment && (
                    <Row k="Komentarz">
                      <p className="text-slate-800 whitespace-pre-wrap">
                        {detail.data.evidenceComment}
                      </p>
                    </Row>
                  )}

                  {!detail.data.isActive && (
                    <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
                      Weryfikacja nieaktywna
                      {detail.data.invalidatedReason && (
                        <> ({invalidationReasonLabel(detail.data.invalidatedReason)})</>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>

            <footer className="px-5 py-3 border-t border-slate-200 bg-slate-50/60 flex justify-end gap-2 rounded-b-xl">
              <button
                type="button"
                onClick={onClose}
                className="rounded-md border border-slate-300 bg-white px-3 py-1.5 text-sm font-medium text-slate-700 hover:bg-slate-50"
              >
                Zamknij
              </button>
              {canRevoke && detail.data?.isActive && (
                <button
                  type="button"
                  onClick={() => setShowRevoke(true)}
                  className="rounded-md border border-red-200 bg-white px-3 py-1.5 text-sm font-medium text-red-700 hover:bg-red-50"
                >
                  Cofnij weryfikację
                </button>
              )}
            </footer>
          </Dialog.Content>
        </Dialog.Portal>
      </Dialog.Root>

      {showRevoke && (
        <RevokeVerificationDialog
          verificationId={verificationId}
          subjectType={subjectType}
          subjectId={subjectId}
          onClose={() => setShowRevoke(false)}
          onRevoked={onClose}
        />
      )}
    </>
  )
}

function Row({ k, children }: { k: string; children: React.ReactNode }) {
  return (
    <div className="grid grid-cols-[140px_1fr] gap-3 items-start text-sm">
      <dt className="text-[11px] font-medium uppercase tracking-[0.06em] text-slate-500 pt-0.5">
        {k}
      </dt>
      <dd className="min-w-0 text-slate-800">{children}</dd>
    </div>
  )
}

function ModeBadge({ mode }: { mode: VerificationMode }) {
  const Icon = mode === 'CLICK' ? Check : mode === 'DOCUMENT' ? FileText : MessageSquare
  const cls = mode === 'CLICK'
    ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
    : mode === 'DOCUMENT'
    ? 'bg-sky-50 text-sky-700 border-sky-200'
    : 'bg-amber-50 text-amber-700 border-amber-200'
  return (
    <span className={`inline-flex items-center gap-1 rounded border px-2 py-0.5 text-xs font-medium ${cls}`}>
      <Icon className="h-3 w-3" />
      {VERIFICATION_MODE_LABELS[mode]}
    </span>
  )
}

function pluralPliki(n: number): string {
  if (n === 1) return 'plik'
  const lastTwo = n % 100
  const last = n % 10
  if (lastTwo >= 12 && lastTwo <= 14) return 'plików'
  if (last >= 2 && last <= 4) return 'pliki'
  return 'plików'
}

function invalidationReasonLabel(r: string): string {
  if (r === 'VALUE_CHANGED') return 'wartość pola się zmieniła'
  if (r === 'MANUAL_REVOKE') return 'cofnięta ręcznie'
  return r
}
